import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { traderSetsApi, type TraderSet, type TraderSetCriteria } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUiMode } from '@/hooks/useUiMode';
import { useSyncedPreference } from '@/hooks/useSyncedPreference';

/****************
 * GROUPS AND CATEGORIES, phase 1 (Guillaume 2026-10-26; plan in the vault,
 * "Groups and Categories — Portfolio of Traders, Plan (2026-10-25)").
 *
 * Who sees them: admins only until Guillaume releases them, and Pro only for
 * good (his answer 7: groups and categories do not exist in Lite).
 *
 * THE LENS: one choice per user, shared by the Traders and Asset pages: which
 * set's positioning the pages describe (null = every trader). Kept as a synced
 * preference so it follows the user to the phone.
 ****************/

export const LENS_KEY = 'copyfi.traderSets.lens';

/** May this viewer use groups and categories at all? */
export function useTraderSetsEnabled(): boolean {
  const { isAdmin } = useAuth();
  const { mode } = useUiMode();
  return mode === 'pro';
}

export function useTraderSets() {
  const enabled = useTraderSetsEnabled();
  return useQuery({
    queryKey: ['trader-sets'],
    queryFn: async () => (await traderSetsApi.list()).sets,
    enabled,
    staleTime: 60_000,
  });
}

export function useTraderSet(id: number | null | undefined) {
  const enabled = useTraderSetsEnabled();
  return useQuery({
    queryKey: ['trader-set', id],
    queryFn: () => traderSetsApi.get(id as number),
    enabled: enabled && id != null,
    staleTime: 60_000,
  });
}

/** Every write refreshes the list, the set itself and anything read through a lens. */
export function useTraderSetMutations() {
  const qc = useQueryClient();
  const settle = (s?: TraderSet) => {
    qc.invalidateQueries({ queryKey: ['trader-sets'] });
    if (s) qc.setQueryData(['trader-set', s.id], s);
    qc.invalidateQueries({ predicate: (q) => q.queryKey.includes('lens') });
  };
  return {
    create: useMutation({
      mutationFn: (b: { kind: 'group' | 'category'; name: string; description?: string; members?: string[]; criteria?: TraderSetCriteria }) => traderSetsApi.create(b),
      onSuccess: settle,
    }),
    update: useMutation({
      mutationFn: ({ id, ...b }: { id: number; name?: string; description?: string; members?: string[]; criteria?: TraderSetCriteria }) => traderSetsApi.update(id, b),
      onSuccess: settle,
    }),
    remove: useMutation({
      mutationFn: (id: number) => traderSetsApi.remove(id),
      onSuccess: () => settle(),
    }),
    members: useMutation({
      mutationFn: ({ id, add, remove }: { id: number; add?: string[]; remove?: string[] }) => traderSetsApi.members(id, { add, remove }),
      onSuccess: settle,
    }),
  };
}

/**
 * The lens in use: its id (null = everyone), the set, and its members as a
 * lookup. A lens whose set was deleted reads as everyone; so does any viewer
 * groups are not open to, whatever is saved.
 */
export function useLens() {
  const enabled = useTraderSetsEnabled();
  const [saved, setSaved] = useSyncedPreference<number | null>(LENS_KEY, null);
  const sets = useTraderSets();
  const chosen = enabled && saved != null && (sets.data ?? []).some((s) => s.id === saved) ? saved : null;
  const set = useTraderSet(chosen);
  const members = useMemo(() => (set.data?.members ? new Set(set.data.members) : null), [set.data]);
  return {
    /** The id to send as ?set=, or null for everyone. */
    setId: chosen,
    set: set.data ?? null,
    /** The members, or null for everyone (also while the set loads). */
    members: chosen != null ? members : null,
    loading: chosen != null && set.isLoading,
    choose: (id: number | null) => setSaved(id),
  };
}

/** The query-key part and the URL part for a lensed request. */
export function lensKey(setId: number | null): (string | number)[] {
  return setId != null ? ['lens', setId] : [];
}
export function lensParam(setId: number | null, first = false): string {
  return setId != null ? `${first ? '?' : '&'}set=${setId}` : '';
}
